import type { dia } from '@joint/core'
import { routeLayerToCategory } from '../../../map-editor-shared/lib/layerUtils'

/**
 * 구역(zone) ↔ CCTV 포함 관계.
 *
 * 정책 (사용자 요청): 존을 그리면 그 폴리곤 **안에 든 모든 CCTV** 를 반경 없이
 * 자동으로 연결한다. 존 생성 시 1회 계산하며, 양방향으로 저장한다.
 *   - zone.data.properties.cctvIds = [포함된 cctv id...]
 *   - 각 cctv.data.properties.zoneId = zone id
 *
 * 두 키 모두 `*Ids` / `*Id` 패턴이라 export 시 relationshipExtractor 가
 * 그대로 passthrough 한다(zone / cctv 스키마 모두 .passthrough()).
 */

/** 존이 보유한 CCTV id 목록 키. Zone export 스키마로 passthrough. */
export const ZONE_CCTV_PROPERTY_KEY = 'cctvIds'
/** CCTV 가 보유한 소속 zone id 키. CCTV export 스키마로 passthrough. */
export const CCTV_ZONE_PROPERTY_KEY = 'zoneId'

export interface XYPoint {
  x: number
  y: number
}

export interface ZoneCctvLinkResult {
  zoneId: string
  cctvIds: string[]
}

/**
 * Ray-casting point-in-polygon. polygon 은 절대 좌표 정점 배열(>=3).
 * 경계선 위 점은 구현 특성상 불안정하므로 "중심점" 판정에만 사용한다.
 */
export function pointInPolygon(point: XYPoint, polygon: XYPoint[]): boolean {
  if (polygon.length < 3) return false
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x
    const yi = polygon[i].y
    const xj = polygon[j].x
    const yj = polygon[j].y
    const intersect =
      (yi > point.y) !== (yj > point.y) &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

/** data.layer → category, 없으면 data.type 으로 폴백 (buildMapData 와 동일 규칙). */
export function elementCategory(element: dia.Element): string {
  const data = (element.get('data') as Record<string, unknown>) || {}
  let category = 'unknown'
  if (data.layer) category = routeLayerToCategory(String(data.layer))
  if (category === 'unknown' && data.type) {
    category = routeLayerToCategory(String(data.type))
  }
  return category
}

/** 조명/단방향 CCTV 둘 다 포함. */
export function isCctvElement(element: dia.Element): boolean {
  const c = elementCategory(element)
  return c === 'cctv' || c === 'warningCctv'
}

/**
 * 존 셀의 절대 폴리곤 정점. data.points({x,y}) 우선, vertices([x,y]) 폴백,
 * 최후엔 bbox 사각형. createZoneCell 이 둘 다 저장하므로 보통 points 사용.
 */
export function getZonePolygon(zoneElement: dia.Element): XYPoint[] {
  const data = (zoneElement.get('data') as Record<string, unknown>) || {}
  const pts = data.points
  if (Array.isArray(pts) && pts.length >= 3) {
    return pts.map((p: { x: number; y: number }) => ({
      x: Number(p.x),
      y: Number(p.y),
    }))
  }
  const verts = data.vertices
  if (Array.isArray(verts) && verts.length >= 3) {
    return verts.map((v: [number, number]) => ({
      x: Number(v[0]),
      y: Number(v[1]),
    }))
  }
  const b = zoneElement.getBBox()
  return [
    { x: b.x, y: b.y },
    { x: b.x + b.width, y: b.y },
    { x: b.x + b.width, y: b.y + b.height },
    { x: b.x, y: b.y + b.height },
  ]
}

/** 존 폴리곤 안에 중심점이 든 CCTV 엘리먼트들. */
export function findCctvElementsInZone(
  graph: dia.Graph,
  zoneElement: dia.Element,
): dia.Element[] {
  const polygon = getZonePolygon(zoneElement)
  if (polygon.length < 3) return []
  return graph.getElements().filter((el) => {
    if (el.id === zoneElement.id) return false
    if (!isCctvElement(el)) return false
    const center = el.getBBox().center()
    return pointInPolygon({ x: center.x, y: center.y }, polygon)
  })
}

/** immutable 하게 data.properties[key] = value 로 단일 id 설정. */
function setSingleRelation(
  element: dia.Element,
  key: string,
  value: string,
): void {
  const data = (element.get('data') as Record<string, unknown>) || {}
  const props = (data.properties as Record<string, unknown>) || {}
  if (props[key] === value) return
  element.set('data', { ...data, properties: { ...props, [key]: value } })
}

/**
 * 존 폴리곤 안에 든 모든 CCTV 를 존과 양방향으로 연결한다(존 생성 시 1회).
 * - zone.data.properties.cctvIds 를 포함된 CCTV id 들로 설정(replace)
 * - 각 cctv.data.properties.zoneId 를 zone id 로 설정
 * 반환된 cctvIds 로 시각화/저장에 사용한다.
 */
export function linkZoneToContainedCctvs(
  graph: dia.Graph,
  zoneElement: dia.Element,
): ZoneCctvLinkResult {
  const zoneId = String(zoneElement.id)
  const cctvs = findCctvElementsInZone(graph, zoneElement)
  const cctvIds = cctvs.map((c) => String(c.id))

  const zData = (zoneElement.get('data') as Record<string, unknown>) || {}
  const zProps = (zData.properties as Record<string, unknown>) || {}
  zoneElement.set('data', {
    ...zData,
    properties: { ...zProps, [ZONE_CCTV_PROPERTY_KEY]: cctvIds },
  })

  for (const cctv of cctvs) {
    setSingleRelation(cctv, CCTV_ZONE_PROPERTY_KEY, zoneId)
  }

  return { zoneId, cctvIds }
}

/** data.properties.kind === 'zone' 인 모든 존 엘리먼트. */
export function findZoneElements(graph: dia.Graph): dia.Element[] {
  return graph.getElements().filter((el) => {
    const data = (el.get('data') as Record<string, unknown>) || {}
    const props = (data.properties as Record<string, unknown>) || {}
    return props.kind === 'zone'
  })
}

